import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  where,
  updateDoc,
  serverTimestamp,
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION_NAME = 'bookmarks';

export const bookmarkService = {
  // Check if a bookmark with the given URL already exists for the user
  async checkIfBookmarkExists(url, userId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        where('url', '==', url)
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty; // Returns true if a bookmark with the URL exists
    } catch (error) {
      console.error('Error checking if bookmark exists:', error);
      throw error; // Re-throw to allow caller to handle
    }
  },

  // Add a new bookmark
  async addBookmark(bookmarkData, userId) {
    try {
      // Check if a bookmark with this URL already exists for this user
      const exists = await this.checkIfBookmarkExists(bookmarkData.url, userId);
      if (exists) {
        // console.warn('Bookmark with this URL already exists in Firestore. Not adding.');
        // Instead of just logging, let's throw an error or return a specific indicator
        // so the caller (offlineBookmarkService) can handle it.
        const error = new Error('Bookmark with this URL already exists in Firestore.');
        error.code = 'duplicate-url'; // Custom error code
        throw error;
      }

      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...bookmarkData,
        userId: userId, // Associate with user
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: docRef.id, ...bookmarkData, userId };
    } catch (error) {
      console.error('Error adding bookmark:', error);
      throw error;
    }
  },

  // Get all bookmarks for a specific user
  async getBookmarks(userId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME), 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting bookmarks:', error);
      throw error;
    }
  },

  // Search bookmarks for a specific user
  async searchBookmarks(searchTerm, userId) {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This is a simple title search - for better search, consider using Algolia
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        where('title', '>=', searchTerm),
        where('title', '<=', searchTerm + '\uf8ff'),
        orderBy('title'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error searching bookmarks:', error);
      throw error;
    }
  },

  // Update bookmark (only if user owns it)
  async updateBookmark(id, updates, userId) {
    try {
      const bookmarkRef = doc(db, COLLECTION_NAME, id);
      
      // First check if the bookmark belongs to the user
      const bookmarkSnap = await getDocs(query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId)
      ));
      
      const userBookmark = bookmarkSnap.docs.find(doc => doc.id === id);
      if (!userBookmark) {
        throw new Error('Bookmark not found or you do not have permission to edit it');
      }
      
      await updateDoc(bookmarkRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { id, ...updates };
    } catch (error) {
      console.error('Error updating bookmark:', error);
      throw error;
    }
  },

  // Delete bookmark (only if user owns it)
  async deleteBookmark(id, userId) {
    try {
      // First check if the bookmark belongs to the user
      const bookmarkSnap = await getDocs(query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId)
      ));
      
      const userBookmark = bookmarkSnap.docs.find(doc => doc.id === id);
      if (!userBookmark) {
        throw new Error('Bookmark not found or you do not have permission to delete it');
      }
      
      await deleteDoc(doc(db, COLLECTION_NAME, id));
      return id;
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      throw error;
    }
  },

  // Listen to real-time updates for bookmarks
  onBookmarksSnapshot(userId, onNext, onError) {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const bookmarks = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        onNext(bookmarks); // Call the onNext callback with the new data
      },
      (error) => {
        console.error('Error in onBookmarksSnapshot:', error);
        if (onError) {
          onError(error); // Call the onError callback if provided
        }
      }
    );

    return unsubscribe; // Return the unsubscribe function
  }
};
